/**
 * Traducción entre lo que el usuario decidió en el túnel y lo que entiende `POST business-unit/add-bulk`,
 * en los dos sentidos: el pedido y sus errores.
 *
 * Vive aparte de `api.js` porque es donde están las dos trampas del contrato: la cuenta se
 * identifica con DOS ids distintos según a quién se le hable, y los errores viajan dentro de un
 * string.
 */
import {suggestedMonthlyFor} from './budgetMath';
import {choosesGoogleConversion, conversionFor, googleActionsFor, needsConversion} from './conversionSuggestion';

/**
 * Las propuestas identifican cada cuenta por `key`, que es la clave primaria de la cuenta en Dashbo;
 * `add-bulk` en cambio espera `account_id`, que es el id que le puso la plataforma. Son distintos y
 * los dos son números largos, así que confundirlos no rompe nada visible: crea líneas sobre las
 * cuentas equivocadas o falla con "la cuenta no está disponible".
 */
export function accountsByPk(candidates) {
    const byPk = {};
    (candidates?.accounts || []).forEach(account => {
        byPk[String(account.id)] = account;
    });
    return byPk;
}

/**
 * El cuerpo del POST y, al lado, con qué cuenta se corresponde cada posición.
 *
 * `indexToPk` es lo que después permite pintar el error del ítem 7 sobre la tarjeta que el usuario
 * está mirando: el backend contesta por índice y la pantalla razona por cuenta.
 *
 * Un mismo `new_client_name` repetido en varios ítems es deliberado: el backend crea UN cliente y le
 * cuelga todas sus cuentas. Es cómo el túnel arma un cliente con varias plataformas.
 */
export function buildAddBulkPayload({decisions, candidates, budgets, budgetEdits, conversionEvents, conversionEdits}) {
    const byPk = accountsByPk(candidates);
    const items = [];
    const indexToPk = [];
    // La moneda de respaldo para las cuentas que no declaran la suya. Es `null` si la cartera tiene
    // más de una: ahí no hay respaldo posible que no sea una adivinanza.
    const fallbackCurrency = soleCurrencyOf(candidates, budgets);

    Object.keys(decisions?.byAccount || {}).forEach(pk => {
        const decision = decisions.byAccount[pk];
        const account = byPk[pk];
        // `dismissed` también corta, y es la última red: una cuenta puede quedar `include: true` y
        // `dismissed: true` a la vez —pasaba al reagrupar con el toggle— y entonces no se ve en
        // ninguna pantalla pero el POST le crea cliente y línea igual. Lo que el usuario sacó no
        // viaja, aunque algún camino se haya olvidado de apagarle el tilde.
        if (!decision?.include || decision.dismissed || !decision.clientRef || !account) return;

        const item = {channel: account.channel, account_id: account.account_id};
        if (decision.clientRef.type === 'existing') {
            item.agency_client_id = decision.clientRef.id;
        } else {
            item.new_client_name = clientNameOf(decision.clientRef, decisions);
        }

        const budget = budgetForAccount({pk, budgets, budgetEdits, fallbackCurrency});
        if (budget) item.budget = budget;

        // La conversión viaja sólo si hay una: sin el campo el backend crea la línea como siempre, y
        // es también lo que pasa con «la elijo después».
        if (needsConversion(account)) {
            const {actionIds} = conversionFor({pk, conversionEvents, conversionEdits});
            if (actionIds.length) item.selected_actions = actionIds;
        } else if (choosesGoogleConversion(account)) {
            // Google (C-01): sólo si el usuario eligió. Sin la clave, la línea nace con todas las
            // primarias, como siempre.
            const actionIds = googleActionsFor(pk, conversionEdits);
            if (actionIds.length) item.selected_actions = actionIds;
        }

        indexToPk.push(pk);
        items.push(item);
    });

    return {items, indexToPk};
}

/** El nombre con el que se va a crear el cliente, con el renombre del usuario aplicado si lo hubo. */
export function clientNameOf(clientRef, decisions) {
    const renamed = decisions?.clientRenames?.[clientRef.name];
    return (renamed || clientRef.name || '').trim();
}

/**
 * El presupuesto que se manda para esa cuenta, o `null` si nace sin presupuesto.
 *
 * Nacer sin presupuesto es un caso VÁLIDO, no un error: la cuenta que no gastó el mes pasado igual
 * merece su línea, y el monto se completa después. Por eso se omite el campo en vez de mandar cero
 * —que el backend rechaza— y por eso una edición a cero también significa "sin presupuesto".
 */
export function budgetForAccount({pk, budgets, budgetEdits, fallbackCurrency}) {
    const edited = budgetEdits?.[pk];
    const suggested = budgets?.[String(pk)];
    // El monto que viaja es EXACTAMENTE el que la pantalla mostró: se resuelve con la misma función
    // (`suggestedMonthlyFor`) y no con una copia. Cuando eran dos, el usuario aprobaba "ARS 160.000
    // estimado desde el presupuesto diario" y la línea nacía sin presupuesto, porque el camino del
    // envío ignoraba el importe diario y no redondeaba.
    const amount = edited ? edited.amount : suggestedMonthlyFor(suggested);
    // La moneda puede faltar en la sugerencia (el backend la devuelve nula si la cuenta no la
    // declara). Si el usuario igual escribió un monto, se usa la de su cartera antes que descartar
    // en silencio lo único que la pantalla le ofreció hacer.
    const currency = (edited && edited.currency) || suggested?.currency || fallbackCurrency;

    if (!currency) return null;
    const numeric = Number(amount);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return {amount: numeric, currency};
}

/** Los códigos que el backend garantiza que llegan ANTES de escribir nada. */
/** El pedido entero no entra en el cupo de cuentas del plan (`add_bulk.ERROR_PLAN_AD_ACCOUNT_LIMIT`). */
export const PLAN_AD_ACCOUNT_LIMIT = 'PLAN_AD_ACCOUNT_LIMIT';

/** Errores del pedido que el backend emite en `can_execute`, sin haber escrito nada. */
export const REQUEST_ERRORS_BEFORE_WRITING = [PLAN_AD_ACCOUNT_LIMIT, 'NO_ITEMS', 'INVALID_PAYLOAD'];

export const VALIDATION_ERROR_CODES = [
    'NO_ITEMS', 'MISSING_CLIENT', 'CLIENT_NOT_FOUND', 'CLIENT_NAME_TAKEN',
    'CLIENT_NAME_TOO_LONG', 'AD_ACCOUNT_NOT_AVAILABLE', 'INVALID_BUDGET', 'INVALID_CONVERSION_EVENT',
];

/**
 * Lo que le pasó al pedido, leído desde la respuesta cruda del backend.
 *
 * Dos cosas que no se ven en el JSON y hay que saber: `errors` llega como un STRING con JSON
 * adentro (lo serializa el ErrorResponse compartido de 150 endpoints, no se toca), y un error puede
 * venir sin `index` cuando falló ya escribiendo. Sin índice la pantalla no puede señalar ninguna
 * tarjeta, así que ese caso se trata aparte: se avisa en general y se vuelve a preguntar el estado.
 *
 * Devuelve `{byPk, unassigned, createdClients, isPartial}`.
 */
export function parseAddBulk422(response, indexToPk) {
    const parsed = safeParseErrors(response);
    const items = parsed.items || [];
    const byPk = {};
    const unassigned = [];

    items.forEach(item => {
        const pk = item.index === null || item.index === undefined ? null : indexToPk?.[item.index];
        // `details` trae los números de los errores que los tienen (el cupo del plan: `limit`, `used`,
        // `requested_new`), para decir cuántas cuentas sobran sin leer el mensaje del backend.
        const error = {code: item.code, message: item.message};
        if (item.details && Object.keys(item.details).length) error.details = item.details;
        if (pk === null || pk === undefined) {
            unassigned.push(error);
            return;
        }
        byPk[pk] = (byPk[pk] || []).concat([error]);
    });

    return {
        byPk,
        unassigned,
        // Los errores del PEDIDO, no de una fila (el cupo del plan). Hasta el 24/09 se calculaban y
        // no llegaban a ningún lado: el cartel sólo mira `byPk`, así que se veía «falló» sin motivo.
        requestErrors: unassigned,
        createdClients: parsed.created_clients || [],
        // Si algún error no se pudo ubicar, el pedido puede haber quedado a medias: hay clientes y
        // vínculos que el backend no revierte. La pantalla tiene que volver a leer el estado real en
        // vez de confiar en lo que tenía en memoria.
        // Menos los que el backend rechaza ANTES de escribir: ahí no quedó nada a medias, y titular
        // «quedó a medias» sobre un pedido que no tocó la base es mentirle al usuario.
        isPartial: unassigned.some(error => !REQUEST_ERRORS_BEFORE_WRITING.includes(error.code)),
    };
}

/**
 * La moneda con la que trabaja esta cartera, **sólo si trabaja con una sola**.
 *
 * Sirve de respaldo para la cuenta que no declara moneda (`AdAccount._currency` es nullable) cuando
 * el usuario igual le escribió un monto. Con una cartera de una sola moneda, asumir que quien opera
 * en pesos escribió pesos no es inventar: es lo único que puede ser.
 *
 * ⚠️ Con DOS o más monedas sí sería inventar, y caro: la línea nacería con un presupuesto en la
 * moneda de otro cliente, sin que nada lo delate — el número se ve razonable, y a partir de ahí el
 * pacing y las alertas de esa línea comparan contra una escala que no es la suya. Ante la duda se
 * devuelve `null` y la línea nace **sin** presupuesto, que es un final válido del túnel y, sobre
 * todo, visible y corregible.
 */
export function soleCurrencyOf(candidates, budgets) {
    const found = new Set();
    Object.values(budgets || {}).forEach(budget => {
        if (budget?.currency) found.add(budget.currency);
    });
    (candidates?.accounts || []).forEach(account => {
        if (account.currency) found.add(account.currency);
    });
    return found.size === 1 ? Array.from(found)[0] : null;
}

/** Cuántas monedas distintas hay en la cartera. La pantalla lo usa para avisar antes de bloquear. */
export function currenciesOf(candidates, budgets) {
    const found = new Set();
    Object.values(budgets || {}).forEach(budget => {
        if (budget?.currency) found.add(budget.currency);
    });
    (candidates?.accounts || []).forEach(account => {
        if (account.currency) found.add(account.currency);
    });
    return Array.from(found);
}

function safeParseErrors(response) {
    const raw = response?.errors;
    if (!raw) return {};
    if (typeof raw !== 'string') return raw;
    try {
        return JSON.parse(raw);
    } catch (error) {
        return {};
    }
}
